import type {
  DirectiveResult,
  SYSAction,
  SYSDialog,
  SYSForm,
  SYSState,
  TemplateResult,
  UnsafeHTMLDirective,
} from "../../"
import { currentScreenAndDialog, SYSConfig } from "../../"
import { T } from "../../textapi"

import { HookDialogClose, HookDialogClose_Type } from "../../global/hooks"
import { _handleDialogVisible } from "../../helpers"

import { AddGlobalReactor } from "../../tracker/reactor"

import type { OpenModalDialogState, ResultType } from "./dialog"

import type {
  DialogsState,
  DialogType,
  FormHandlingType,
} from "./sysdialogbase"

import { _setDialogVisible } from "../forms/helper"
import { FormInit } from "../forms/formapi"

export type OpenDialogOptions = {
  dialogType: SYSDialog
  formsHandling?: FormHandlingType[]
  modal?: boolean
  data?: any
  instance?: any
}
export const _DialogClose: SYSAction<
  | SYSDialog
  | {
      dialog: SYSDialog
      forceElementIdToFocusAfterClose?: string
      resolveValue?: any
      instance?: any
    }
> = async (value, { global }) => {
  let state = global.getState()
  let actions = global.getActions()
  let dlgState: DialogsState
  let resolveValue

  let dialog

  if (typeof value === "string") {
    dialog = value
    dlgState = state.ovl.dialogs[value].instance[value]
    if (!dlgState.visible) {
      return
    }
  } else {
    if (value.resolveValue) {
      resolveValue = value.resolveValue
    }

    dialog = value.dialog
    if (!value.instance) {
      value.instance = dialog
    }
    dlgState = state.ovl.dialogs[value.dialog].instance[value.instance]
    // dlgState.forceElementIdToFocusAfterClose =
    //   value.forceElementIdToFocusAfterClose
  }
  if (!dlgState?.visible) {
    return
  }
  if (dlgState.closing) {
    return
  }
  let fnName = HookDialogClose
  let fn = actions.app.global.hooks?.[fnName]
  if (fn) {
    if (!(await fn(<HookDialogClose_Type>{ dialog, dialogState: dlgState }))) {
      return
    }
  }
  // // also set class for fadeout directly in dom
  // // maybe refactor to reactor and remove the parts in baselement which are also doing this
  // // its good to have it here because css animations are in parallel to long running actions (stichwort: headermenu sorting click)
  // let el = document.querySelector("ovl-dialogholder .fd-message-box")
  // el.classList.add("fadeOutDialog")
  dlgState.closing = true
  if (dlgState.modal && dlgState.resolver) {
    for (let r = 0; r < dlgState.resolver.length; r++) {
      dlgState.resolver[r](resolveValue)
    }

    dlgState.resolver = []
  }
}

export const _DialogSetLock: SYSAction<{
  dialog: SYSDialog
  locked: boolean
  instance?: any
}> = (value, { global }) => {
  let state = global.getState()
  if (!value.instance) {
    value.instance = value.dialog
  }
  if (state.ovl.dialogs[value.dialog].instance[value.instance]) {
    state.ovl.dialogs[value.dialog].instance[value.instance].disabled =
      value.locked
  }
}

export const _DialogOpen: SYSAction<OpenDialogOptions, Promise<any>> = async (
  value,
  { global }
) => {
  let state = global.getState()
  return await DialogOpenHelper(<OpenDialogOptions>value, state)
}

const DialogOpenHelper = async (value: OpenDialogOptions, state: SYSState) => {
  if (!value.instance) {
    value.instance = value.dialogType
  }
  let dlgState = state.ovl.dialogs[value.dialogType]

  while (dlgState.instance[value.instance]?.closing === true) {
    _setDialogVisible(false, dlgState.instance[value.instance])
  }

  let zIndex = Object.keys(currentScreenAndDialog.dialogs).reduce((val, k) => {
    let dialogState = currentScreenAndDialog.dialogs[k].dialogState
    if (dialogState) {
      let z = dialogState.zIndex
      if (z > val) {
        val = z
      }
    }
    return val
  }, 0)
  zIndex++
  if (zIndex < 6) {
    zIndex = zIndex + 6
  }
  if (!dlgState.instance[value.instance]) {
    dlgState.instance[value.instance] = {
      visible: false,
      closing: false,
      focus: {
        focusSet: false,
        focusWhenLeft: undefined,
      },
      zIndex,
      formsHandling: value.formsHandling,
      dialog: value.dialogType,
      instance: value.instance,
      resolver: [],
    }
  }

  if (value.formsHandling) {
    await Promise.all(
      value.formsHandling
        .filter((f) => f.creation)
        .map((m) => {
          if (m.creation.autoInitFormFields === undefined) {
            m.creation.autoInitFormFields = {}
          }
          return FormInit(m.formType, m.creation.autoInitFormFields, {
            formId: m.formId,
            parentFieldKey: m.creation.parentFieldKey,
            parentFormType: m.creation.parentFormType,
            parentFormId: m.creation.parentFormId,
            initialFocusFieldKey: m.creation.initialFocusFieldKey,
          })
        })
    )
  }

  AddGlobalReactor(
    [`ovl.dialogs.${value.dialogType}.instance.${value.instance}.visible`],
    _handleDialogVisible
  )
  let instance = dlgState.instance[value.instance]
  if (instance.visible && !instance.closing) {
    if (value.modal && instance.resolver.length > 0) {
      return new Promise<any>((resolve) => {
        instance.resolver.push(resolve)
      })
    }
    return
  }
  // sanitize formsHandling..If there is no formId given use formType
  if (value.formsHandling) {
    value.formsHandling.forEach((f) => {
      if (!f.formId) {
        f.formId = f.formType
      }
    })
    instance.formsHandling = value.formsHandling
    if (instance.formsHandling && !instance.mainFormId) {
      // if there was not giveen a formId explicitly for the main form of the dialog
      // but there was give am formsHandling... use formId from the first form in the array
      instance.mainFormId = instance.formsHandling[0].formId
    }
  }
  instance.modal = value.modal
  instance.data = value.data
  instance.closing = false
  _setDialogVisible(true, instance)
  //}, wait)
  let p
  if (value.modal) {
    p = new Promise<any>((resolve, reject) => {
      instance.resolver.push(resolve)
    })
  } else {
    p = Promise.resolve(true)
  }
  return p
}

export const _ModalDialogOpen: SYSAction<OpenModalDialogState> = async (
  value,
  { global }
) => {
  let state = global.getState()
  let actions = global.getActions()
  if (!value.instance) {
    value.instance = "SYSModal"
  }
  state.ovl.libState.dialog[value.instance] = {
    type: value.type,
    customClass: value.customClass,
    title: value.title,
    default: value.default,
    instance: value.instance,
    buttonText_02: undefined,
    buttonText_01: undefined,
    result: undefined,
    text: undefined,
    connectedForm: value.connectedForm,
  }
  let dlgState = state.ovl.libState.dialog[value.instance]
  if (value.cancel !== "NoButton") {
    dlgState.buttonText_02 = state.ovl.language.translations[value.cancel]
    if (!dlgState.buttonText_02) {
      dlgState.buttonText_02 = value.cancel
    }
  } else {
    dlgState.buttonText_02 = ""
  }
  if (value.ok !== "NoButton") {
    dlgState.buttonText_01 = state.ovl.language.translations[value.ok]
    if (!dlgState.buttonText_01) {
      dlgState.buttonText_01 = value.ok
    }
  } else {
    dlgState.buttonText_01 = ""
  }

  if (value.freeButton1) {
    dlgState.buttonText_03 = state.ovl.language.translations[value.freeButton1]
    if (!dlgState.buttonText_03) {
      dlgState.buttonText_03 = value.freeButton1
    }
  }

  dlgState.text = value.text
  dlgState.result = undefined

  let formsHandling: FormHandlingType[]

  if (dlgState.connectedForm) {
    formsHandling = [
      {
        formType: <SYSForm>dlgState.connectedForm.formType,
        formId: dlgState.connectedForm.formId,
        isConnectedToDialog: true,
      },
    ]
  }

  await actions.ovl.dialog._DialogOpen({
    dialogType: "SYSModal",
    instance: value.instance,
    //forceElementIdToFocusAfterOpen,
    modal: true,
    formsHandling,
  })
}

type OkCancelDialog = {
  text: string | TemplateResult | UnsafeHTMLDirective | DirectiveResult
  default: ResultType
  type?: DialogType
  customClass?: string
  title?: string | TemplateResult | UnsafeHTMLDirective | DirectiveResult
  buttonText_01?: string
  buttonText_02?: string
  buttonText_03?: string
  instance?: any
  connectedForm?: { formType: string; formId: string }
}
type OkDialog = {
  text: string | TemplateResult | UnsafeHTMLDirective | DirectiveResult
  type?: DialogType
  customClass?: string
  title?: string | TemplateResult | UnsafeHTMLDirective | DirectiveResult
  buttonText_01?: string
  instance?: any
  connectedForm?: { formType: string; formId: string }
}
export const _OkCancelDialog: SYSAction<OkCancelDialog> = async (
  value,
  { global }
) => {
  let state = global.getState()
  let actions = global.getActions()
  let type: DialogType = "confirmation"
  if (value.type) {
    type = value.type
  }
  let title

  if (value.title) {
    title = value.title
  } else {
    if (SYSConfig.global?.defaultDialogTitle) {
      title = T(SYSConfig.global?.defaultDialogTitle)
    } else {
      title = T("SYSAppTitle")
    }
  }
  let customClass = ""
  if (value.customClass) {
    customClass = value.customClass
  }

  await actions.ovl.dialog._ModalDialogOpen({
    title,
    customClass,
    type,
    cancel: value.buttonText_02 || "SYSCancel",
    ok: value.buttonText_01 || "SYSOk",
    freeButton1: value.buttonText_03,
    text: value.text,
    default: value.default,
    instance: value.instance,
    connectedForm: value.connectedForm,
  })
}

export const _OkDialog: SYSAction<OkDialog> = async (value, { global }) => {
  let actions = global.getActions()
  let type: DialogType = "information"
  if (value.type) {
    type = value.type
  }
  let customClass = ""
  if (value.customClass) {
    customClass = value.customClass
  }

  let title: any = T("SYSAppTitle")
  if (SYSConfig.global?.defaultDialogTitle) {
    title = T(SYSConfig.global?.defaultDialogTitle)
  }
  if (value.title) {
    title = value.title
  }
  await actions.ovl.dialog._ModalDialogOpen({
    title,
    customClass,
    type,
    cancel: "NoButton",
    ok: value.buttonText_01 || "SYSOk",
    text: value.text,
    default: 1,
    instance: value.instance,
  })
}
